import { defineStore } from "pinia";
import { $axios } from "@bloglovin/vue-framework";
import { useLocalStorage } from "@vueuse/core";

const MFA_REQUIRED = "mfa_required";
const MFA_ENROLLMENT_REQUIRED = "mfa_enrollment_required";

export const MFA_STEP_NONE = "";
export const MFA_STEP_CODE = "code";
export const MFA_STEP_ENROLL = "enroll";
export const MFA_STEP_BACKUP_CODES = "backupCodes";

export const useGeneralStore = defineStore("general-store", {
  state: () => ({
    appId: 4,
    apps: {},
    links: [],
    loadingLogin: false,
    loginErrorMessage: "",
    mfaStep: MFA_STEP_NONE,
    mfaChallengeToken: "",
    mfaErrorMessage: "",
    totpSecret: "",
    totpQrDataUri: "",
    backupCodes: [],
    darkSide: useLocalStorage("Qotsisajak", false),
    audioOn: useLocalStorage("audioOn", true),
  }),
  getters: {
    getApp: state => appKey => {
      if (!Object.keys(state.apps).includes(appKey)) {
        throw new Error(`apps object does not contain an app '${appKey}'`);
      }
      return state.apps[appKey];
    },
  },
  actions: {
    login(email, password) {
      this.loadingLogin = true;
      this.loginErrorMessage = "";
      $axios.post("login", {
        app_id: this.appId,
        email: email,
        password: password,
      }).then(response => {
        if (response.data.success) {
          this.finishLogin();
          return;
        }
        if (response.data.result === MFA_REQUIRED) {
          this.startMfaStep(MFA_STEP_CODE, response.data.challenge_token);
          return;
        }
        if (response.data.result === MFA_ENROLLMENT_REQUIRED) {
          this.startTotpEnrollment(response.data.challenge_token);
          return;
        }
        if (response.data.old_password) {
          this.loginErrorMessage = `You entered an old password, you changed it ${response.data.date_changed}`;
        } else {
          this.loginErrorMessage = "login failed";
        }
        this.loadingLogin = false;
      }).catch(error => {
        this.loginErrorMessage = this.parseError(error);
        this.loadingLogin = false;
      });
    },
    startMfaStep(step, challengeToken) {
      this.mfaStep = step;
      this.mfaChallengeToken = challengeToken;
      this.mfaErrorMessage = "";
      this.loadingLogin = false;
    },
    startTotpEnrollment(challengeToken) {
      $axios.post("login/enroll-totp", { challenge_token: challengeToken }).then(response => {
        this.totpSecret = response.data.secret;
        this.totpQrDataUri = response.data.qr_svg_data_uri;
        this.startMfaStep(MFA_STEP_ENROLL, challengeToken);
      }).catch(error => {
        this.loginErrorMessage = this.parseError(error);
        this.loadingLogin = false;
      });
    },
    submitMfaCode(code) {
      const route = this.mfaStep === MFA_STEP_ENROLL ? "login/confirm-totp" : "login/verify-code";
      this.loadingLogin = true;
      this.mfaErrorMessage = "";
      $axios.post(route, {
        challenge_token: this.mfaChallengeToken,
        code: code,
      }).then(response => {
        if (response.data.backup_codes && response.data.backup_codes.length) {
          this.backupCodes = response.data.backup_codes;
          this.mfaStep = MFA_STEP_BACKUP_CODES;
          this.loadingLogin = false;
          return;
        }
        this.finishLogin();
      }).catch(() => {
        this.mfaErrorMessage = "That code didn't work. Check your authenticator and try again.";
        this.loadingLogin = false;
      });
    },
    finishLogin() {
      let dest = "/";
      const redirect = new URLSearchParams(window.location.search).get("redirect");
      if (redirect) {
        try {
          const u = new URL(redirect, window.location.origin);
          if (u.origin === window.location.origin) {
            dest = u.pathname + u.search + u.hash;
          }
        } catch (e) {
          dest = "/";
        }
      }
      window.location.href = dest;
    },
    logout() {
      window.location.href = "/logout";
    },
    setApps(apps) {
      this.apps = apps;
    },
    parseError(error) {
      if (error.response.data.message) {
        if (error.response.data.message === "The password you entered is incorrect. Please try again.") {
          return "Incorrect password, please try again.";
        }
      }
      return "Login failed, please try again.";
    },
  },
});
